import type {
  MoveAction,
  RunHorsesPosition,
} from "@cloudnest/run-horses-engine";

/**
 * Typed replay-event adapter for 말달리자 (run-horses).
 *
 * Run-horses is a complete-information game (no per-recipient viewer state and
 * no game-validator projection), so replay renders canonical engine state
 * directly — there is nothing to redact. The only player event is `Move`; the
 * `init` event carries a full state snapshot and is handled by the runner.
 */
export type RunHorsesReplayEventType = "init" | "Move";

export const RUN_HORSES_REPLAY_EVENT_TYPES = [
  "init",
  "Move",
] as const satisfies readonly RunHorsesReplayEventType[];

export function normalizeRunHorsesReplayEventType(
  eventType: string,
): RunHorsesReplayEventType {
  if (
    (RUN_HORSES_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)
  ) {
    return eventType as RunHorsesReplayEventType;
  }
  throw new Error(`Unsupported run-horses event type: ${eventType}`);
}

/**
 * Convert a persisted relay event (everything except `init`) into a typed
 * engine `MoveAction`. Throws on an unsupported event type so a malformed
 * fixture fails loudly rather than silently dropping events.
 */
export function runHorsesEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): MoveAction {
  const type = normalizeRunHorsesReplayEventType(eventType);
  if (type === "init") {
    throw new Error("runHorsesEventToAction cannot convert the init event");
  }
  return {
    type: "Move",
    playerId: payload.playerId === "p2" ? "p2" : "p1",
    from: payload.from as RunHorsesPosition,
    to: payload.to as RunHorsesPosition,
    kind: payload.kind === "knight" ? "knight" : "slide",
  };
}
